import { PointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Image as ImageIcon, Upload, X } from 'lucide-react';
import { errorMessage } from '../lib/testAdminFormat';
import { testApi } from '../lib/testApi';

type CropImageDialogProps = {
  file: File;
  outputSize: number;
  onCancel: () => void;
  onCrop: (file: File) => void;
};

export function CropImageDialog({ file, outputSize, onCancel, onCrop }: CropImageDialogProps) {
  const [source, setSource] = useState<{ url: string; width: number; height: number } | undefined>(undefined);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<{ x: number; y: number; sourceUrl: string }>();
  const drag = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | undefined>(undefined);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => setSource({ url, width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => URL.revokeObjectURL(url);
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const scale = useMemo(() => source ? Math.max(outputSize / source.width, outputSize / source.height) * zoom : 1, [outputSize, source, zoom]);
  const panOffset = source && offset?.sourceUrl === source.url ? offset : { x: 0, y: 0 };
  const position = {
    x: (outputSize - (source?.width ?? outputSize) * scale) / 2 + panOffset.x,
    y: (outputSize - (source?.height ?? outputSize) * scale) / 2 + panOffset.y,
  };

  const move = (event: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    if (source) setOffset({ x: drag.current.offsetX + event.clientX - drag.current.x, y: drag.current.offsetY + event.clientY - drag.current.y, sourceUrl: source.url });
  };
  const stopDragging = () => { drag.current = undefined; };
  const startDragging = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { x: event.clientX, y: event.clientY, offsetX: panOffset.x, offsetY: panOffset.y };
  };
  const crop = async () => {
    if (!source) return;
    const image = new Image();
    image.src = source.url;
    await new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(new Error('The selected file is not a readable image.')); });
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Your browser could not prepare the cropped image.');
    context.drawImage(image, -position.x / scale, -position.y / scale, outputSize / scale, outputSize / scale, 0, 0, outputSize, outputSize);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', .92));
    if (!blob) throw new Error('Your browser could not prepare the cropped image.');
    onCrop(new File([blob], `${file.name.replace(/\.[^/.]+$/, '')}-cropped.jpg`, { type: 'image/jpeg' }));
  };

  return <div className="test-crop-backdrop" role="presentation"><section className="test-crop-dialog" role="dialog" aria-modal="true" aria-labelledby="test-crop-title">
    <div className="test-crop-head"><div><span className="test-kicker">Image crop</span><h2 id="test-crop-title">Frame the answer image</h2><p>Drag the image until the important detail sits inside the square.</p></div><button type="button" className="test-crop-close" onClick={onCancel} aria-label="Close crop editor"><X size={18} /></button></div>
    <div className="test-crop-stage" style={{ width: outputSize, height: outputSize }} onPointerDown={startDragging} onPointerMove={move} onPointerUp={stopDragging} onPointerCancel={stopDragging} onPointerLeave={stopDragging}>
      {source && <img src={source.url} alt="Crop preview" draggable={false} style={{ width: source.width * scale, height: source.height * scale, transform: `translate(${position.x}px, ${position.y}px)` }} />}
      <span className="test-crop-guides" aria-hidden="true" />
    </div>
    <label className="test-crop-zoom"><span>Zoom</span><input type="range" min="1" max="3" step=".01" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} /><output>{Math.round(zoom * 100)}%</output></label>
    <div className="test-crop-actions"><button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button><button type="button" className="btn btn-primary" onClick={() => { void crop(); }}>Use cropped image</button></div>
  </section></div>;
}

export function TestMediaUploadField({ label, value, onChange, accept = 'image/jpeg,image/png,image/webp,image/gif' }: { label: string; value: string; onChange: (value: string) => void; accept?: string }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const selectFile = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const result = await testApi.uploadFile(file);
      onChange(result.imageUrl);
    } catch (reason) {
      setError(errorMessage(reason, 'The asset could not be uploaded.'));
    } finally {
      setUploading(false);
    }
  };
  return <div className="test-media-upload-field"><div className="test-media-upload-head"><span>{label}</span><label className="btn btn-soft"><Upload size={15} />{uploading ? 'Uploading…' : value ? 'Replace' : 'Choose file'}<input className="test-hidden-file" type="file" accept={accept} onChange={(event) => { void selectFile(event.target.files?.[0]); event.currentTarget.value = ''; }} disabled={uploading} /></label></div>{value ? <div className="test-media-upload-preview"><img src={value} alt={`${label} preview`} /><button type="button" onClick={() => onChange('')} aria-label={`Remove ${label}`} title={`Remove ${label}`}><X size={15} /></button></div> : <div className="test-media-upload-empty"><ImageIcon size={17} /><span>No asset selected</span></div>}{error && <small className="test-media-upload-error">{error}</small>}</div>;
}
